<template>
  <main class="mx-auto pt-16 container">
    <CommonBackToButton text-to="About Us" url="/about" class="pt-5 md:pt-12" />

    <div class="mt-6 mb-3 md:mb-8">
      <h1
        class="text-[22px] leading-normal sm:text-[40px] sm:leading-[50px] font-medium mb-4 text-bw-900"
      >
        Meet Our People
      </h1>
    </div>

    <section
      class="mb-10 md:my-16 flex flex-col md:flex-row gap-4 lg:gap-10 relative"
    >
      <div
        class="teams flex flex-row md:flex-col w-full md:w-1/5 gap-3 overflow-auto pb-4 sticky top-[64px] md:top-[100px] md:max-h-[350px] z-10 bg-white pt-3 md:pt-2 justify-between md:justify-normal"
      >
        <template v-if="isLoading">
          <UtilsSkeletonLoading v-for="i in 5" :key="i" class="w-full h-10" />
        </template>
        <template v-else>
          <button
            v-for="(tab, i) in divisionsList"
            :key="i"
            class="text-left text-base md:text-lg hover:text-primary-primary hover:font-medium p-2 hover:border-b-2 hover:border-primary-primary hover:opacity-100 division-list"
            :class="
              activeTab === i
                ? 'text-primary-primary font-medium opacity-100 border-b-2 border-primary-primary'
                : 'font-normal text-bw-700 opacity-50 border-b-2 border-transparent'
            "
            :disabled="activeTab === i"
            @click="switchDivision(i, tab.id)"
          >
            {{ tab.attributes?.title }}
          </button>
        </template>
      </div>
      <div
        class="people grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-12 w-4/5 mx-auto"
      >
        <template v-if="loadingTeam">
          <div v-for="index in 6" :key="index" class="people-card">
            <div class="mb-4 mx-auto relative w-4/5 lg:w-2/3">
              <UtilsSkeletonLoading class="w-full h-40" />
            </div>
            <div class="people-name mb-3">
              <UtilsSkeletonLoading class="mx-auto w-4/5 h-9 mb-3" />
              <UtilsSkeletonLoading class="mx-auto w-3/5 h-8" />
            </div>
          </div>
        </template>
        <template v-else>
          <TransitionGroup name="team">
            <div
              v-for="people in divisionsList[activeTab].attributes.ppl"
              :key="people.id"
              class="people-card"
            >
              <div class="mb-4 mx-auto w-4/5 people-img">
                <NuxtImg
                  v-if="people.attributes.image.data"
                  format="webp"
                  srcset=""
                  :src="people.attributes?.image?.data?.attributes?.url"
                  :alt="people.attributes?.name"
                  :title="people.attributes?.name"
                  loading="lazy"
                  class="m-auto w-full h-full rounded-md"
                />

                <NuxtLink
                  v-if="people.attributes?.linkedin_url"
                  :title="people.attributes?.name"
                  :to="`https://linkedin.com/${people.attributes?.linkedin_url}`"
                  class="linkedin-link"
                >
                  <NuxtIcon
                    name="linkedin"
                    filled
                    class="absolute bottom-1 right-1 z-[2]"
                  />
                </NuxtLink>
              </div>
              <div class="people-name mb-3">
                <h2
                  class="text-bw-900 text-lg md:text-[22px] md:leading-normal text-center font-medium"
                >
                  {{ people.attributes?.name }}
                </h2>
                <p class="text-bw-800 text-sm md:text-base text-center">
                  {{ people.attributes?.title }}
                </p>
              </div>
            </div>
          </TransitionGroup>
          <!-- <div class="mx-auto text-center mt-12">
            <button
              class="px-3 py-[10px] sm:py-3 sm:px-5 text-sm sm:text-base border border-[#FF6700] rounded-md text-[#FF6700] block w-max mx-auto hover:bg-[#FF6700] hover:text-white"
              @click="loadMore"
            >
              Load More
            </button>
          </div> -->
        </template>
      </div>
    </section>
  </main>
</template>

<script setup>
import { useGlobalStore } from '~/store/global'

const global = useGlobalStore()
const mainSeo = global.mainSeo
const mainBanner = global.mainBanner

// const bottomOfWindow = ref(false)

const activeTab = ref(0)
const activeDivision = ref(1)

const isLoading = ref(true)
const loadingTeam = ref(true)
// const isLoadingMore = ref(false)
const route = useRoute()
const SEOMeta = ref({})

const divisionsList = ref([])
const divisionPeople = ref([])
const teamMembers = ref({})

const page = ref(1)
const divisionPeopleMeta = ref({})

const getDivisionPeople = async (division = 1) => {
  const { data, error, pending } = await useSuitmediaApiData(`/api/teams`, {
    query: {
      'pagination[page]': page,
      'pagination[pageSize]': 100,
      'fields[0]': 'id',
      'fields[1]': 'name',
      'fields[2]': 'title',
      'fields[3]': 'linkedin_url',
      'populate[division][fields][0]': 'title',
      'populate[image][fields][1]': 'url',
      'filters[division]': division,
      'sort[1]': 'name'
    }
  })

  loadingTeam.value = pending.value

  if (error.value) {
    throw createError({
      statusCode: 404,
      message: `Team does not exist`
    })
  }

  return data.value
}

const [divisions, peoples, seo] = await Promise.all([
  useSuitmediaApiData(`/api/divisions`, {
    query: {
      'fields[0]': 'id',
      'fields[1]': 'title',
      'fields[2]': 'order',
      'sort[2]': 'order'
    }
  }),
  useSuitmediaApiData(`/api/teams`, {
    query: {
      'pagination[page]': page,
      'pagination[pageSize]': 100,
      'fields[0]': 'id',
      'fields[1]': 'name',
      'fields[2]': 'title',
      'fields[3]': 'linkedin_url',
      'populate[division][fields][0]': 'title',
      'populate[image][fields][1]': 'url',
      'filters[division]': 1,
      'sort[1]': 'name'
    }
  }),
  useSuitmediaApiData(`/api/seo-metas`, {
    query: {
      'filters[url]': route.path,
      populate: 'seo.image'
    }
  })
])

isLoading.value = divisions.pending.value || peoples.pending.value
loadingTeam.value = peoples.pending.value

if (divisions.error.value) {
  throw createError({
    statusCode: 404,
    message: `Division does not exist`
  })
}
if (peoples.error.value) {
  throw createError({
    statusCode: 404,
    message: `Team/People does not exist`
  })
}

SEOMeta.value = seo.data.value.data[0]
divisionsList.value = divisions?.data?.value?.data || []
divisionPeople.value = peoples?.data?.value?.data || []
divisionPeopleMeta.value = peoples?.data?.value?.meta || {}

teamMembers.value = divisionsList.value[activeTab.value]
teamMembers.value.attributes.ppl = divisionPeople.value

useJsonld({
  '@context': 'https://schema.org/',
  '@type': 'Organization',
  name: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  url: SEOMeta?.value?.attributes?.url || 'suitmedia.com',
  description:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  image:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png'
})

useSeoMeta({
  title: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  ogTitle: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  description:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogDescription:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogImage:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png',
  ogType: SEOMeta?.value?.attributes?.seo?.open_graph_type,
  twitterTitle: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  twitterDescription:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  twitterImage:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png'
})

const switchDivision = async (index, divisionId) => {
  activeTab.value = index
  activeDivision.value = divisionId
  if (divisionsList.value[index].attributes.ppl) return

  loadingTeam.value = true
  const peopleData = await getDivisionPeople(divisionId)
  divisionPeople.value = peopleData.data || []
  divisionPeopleMeta.value = peopleData.meta || []

  // To save People Data into state
  teamMembers.value = divisionsList.value.find(div => div.id === divisionId)
  teamMembers.value.attributes.ppl = peopleData.data
}

/*
	GONNA ADD THIS IF NEEDED
*/

// const loadMore = async () => {
//   isLoadingMore.value = true
//   page.value++
//   const addPeople = await getDivisionPeople(activeDivision.value)
//   divisionPeople.value = [...divisionPeople.value, ...addPeople.data]
//   divisionPeopleMeta.value = addPeople.meta
//   isLoadingMore.value = false
// }

// const onScroll = () => {
//   window.onscroll = () => {
//     bottomOfWindow.value =
//       Math.max(
//         window.scrollY,
//         document.documentElement.scrollTop,
//         document.body.scrollTop
//       ) +
//         window.innerHeight ===
// 		document.documentElement.offsetHeight

//     if (
//       bottomOfWindow.value &&
//       divisionPeopleMeta.value.pagination.pageCount >
//         divisionPeopleMeta.value.pagination.page
//     ) {
//       console.log('here')
//       loadMore()
//     }
//   }
// }

// onMounted(() => {
//   onScroll()
// })
/*
	GONNA ADD THIS IF NEEDED
*/
</script>

<style lang="scss" scoped>
.linkedin-link {
  :deep(.nuxt-icon) {
    svg {
      width: 28px;
      height: 28px;
    }
  }
}

.people-img {
  position: relative;
  padding-bottom: 80%;
  display: block;

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
  }
}

.team-enter-from {
  opacity: 0;
  // transform: scale(0.6) rotate(90deg);
  scale: 0.6;
  translate: 0 100px;
}
.team-enter-to {
  opacity: 1;
  // transform: scale(1) rotate(0);
  scale: 1;
  translate: 0;
}
.team-enter-active {
  transition: 0.4s all ease;
}
</style>
